// GameUIOverlay Component
// Extracted from Game.tsx for better HMR and modularity

import { buildShopPurchasePayload, useInteraction } from './contexts/InteractionContext';
import { HudMoodProvider } from './contexts/HudMoodContext';
import React, { useCallback, useEffect } from 'react';
import { DevConfig } from '../../config/environment/devConfig';
import { gameLogger } from '../../utils/GameLogger';
import { Helix2000Character } from '../../shared/CharacterTypes';
import { useGlobalInput } from './hooks/useGlobalInput';
import { windowBridge } from './framework/windowBridge';
import { useWindow } from './framework/WindowManagerContext';

// UI Components
import { HUD } from './HUD/HUD';
import { ConnectionStatusOverlay } from './HUD/ConnectionStatusOverlay';
import { RaceDebugOverlay } from './HUD/RaceDebug/RaceDebugOverlay';
import { FreezeErrorOverlay } from './HUD/FreezeErrorOverlay';
import { BiomeNexusReturn } from './HUD/BiomeNexusReturn';
import { GodModeIndicator } from './HUD/GodModeIndicator';
import { TouchControlsShell } from './mobile';
import { ContextualInteractionPrompt } from './HUD/ContextualInteractionPrompt';

import { EditorOverlay } from './Editor/EditorOverlay';
import { PlayerList } from './HUD/PlayerList';
import { GameMenu } from './HUD/gameMenu/GameMenu';
// Equipment + CharacterStatsUI now share the unified Character window.
import { NPCWindowLayer } from './Systems/NPC/NPCWindowLayer';
import { NPCInteractionUI } from './Systems/NPCInteractionUI';
import { VendorWindow } from './Systems/NPC/VendorWindow';
import { QuestOfferWindow } from './Systems/NPC/QuestOfferWindow';
import { useNpcTransactions } from './hooks/useNpcTransactions';
import { hydrateNpcDialogue } from './npc/hydrateNpcDialogue';
import { npcQuestEntryCount } from './npc/npcQuestEntries';
import { dispatchNpcDialogueAction } from './npc/ferryAction';
import { resolveContextualQuestAction } from '../../shared/logic/contextualQuestAction';
import type { NPC } from '../../shared/NPCData';
// PartyPanel migrated to the unified window engine (Phase 2) — opens via
// windowBridge.toggle('party'); WindowHost mounts PartyContent.
import { TutorialOverlay } from './Tutorial/TutorialOverlay';
import { IntroNarrationOverlay } from './Intro/IntroNarrationOverlay';
// Bank + Crafting + Trade windows migrated to the unified window engine (Phase 2) —
// open via windowBridge.toggle('bank'/'crafting'/'trade'); WindowHost mounts their *Content.
import { Minimap } from './components/Minimap';
import { isScreensaverMode } from '../../config/screensaver/isScreensaverMode';
import { npcMarkerStore } from '../systems/npc/NpcMarkerStore';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { completedQuestStore, objectivesDoneQuestStore } from '../systems/quest/completedQuestStore';
import { npcQuestGreeting } from '../../shared/npc/npcQuestLines';
import { useRecordDiscoveredQuests } from './Windows/Quest/useRecordDiscoveredQuests';
// CastBar (bottom-center React bar) was deleted 2026-06-11 — it polled
// getActiveCast(<auth-layer character id>) which never matches the engine
// caster id (Colyseus sessionId post-connect), so it never rendered. The
// local cast bar is now the OVERHEAD renderer bar (same one remote casters
// get): PlayerManager.syncSpritePosition → LocalCastBarSync →
// PlayerRenderer.updateCastBar (NAMEPLATE.castBar config).

// Lazy-load dev tools and heavy panels (not needed on initial render)
// AbilityBrowser migrated to the unified window engine (Phase 2) — opens via
// windowBridge.toggle('spells'); WindowHost mounts AbilityBrowserContent.
// DevToolsPanel migrated to the unified window engine (Phase 2) — opens via
// windowBridge.toggle('devTools') / F2 / ?diag=1; WindowHost mounts DevToolsContent.
// LobbyToolsPanel migrated to the unified window engine (Phase 2) — opens via
// windowBridge.toggle('lobbyTools'); WindowHost mounts LobbyToolsContent.
// NPCWindowViewer migrated to the unified window engine (Phase 2) — opens via
// windowBridge.toggle('npcWindowViewer') / ?npcViewer=1; WindowHost mounts
// NPCWindowViewerContent.
// SimulationControlPanel migrated to the unified window engine (Phase 2) — opens
// via windowBridge.toggle('simulationPanel'); WindowHost mounts SimulationContent.
// TwitchPiPWindow migrated to the unified window engine (Phase 2) — opens via
// windowBridge.toggle('twitchPiP'); WindowHost mounts TwitchPiPContent.


export interface GameUIOverlayProps {
  player: Helix2000Character | null;
  otherPlayers: Map<string, Helix2000Character>;
  isConnected: boolean;
  isHotbarHydrated?: boolean;
  disconnectReason?: string | null;
  currentLevelName: string;
  currentLevelId: string;
  onAction: (action: string, data?: any) => void;

  showDevActionLogs: boolean;
  isEditorMode: boolean;
  onExitEditor: () => void;
}

export const GameUIOverlay: React.FC<GameUIOverlayProps> = ({
  player,
  otherPlayers,
  isConnected,
  isHotbarHydrated = false,
  disconnectReason,
  currentLevelName,
  currentLevelId,
  onAction,

  isEditorMode,
  onExitEditor
}) => {
  // All gameplay windows now live on the unified engine (Phase 2 complete) and
  // UIContext itself was retired (2026-06-12) — only the interaction context
  // presence is guarded here, same render-bail semantics as before.
  let interaction;
  try {
    interaction = useInteraction();
  } catch {
    return null;
  }

  // Wire up the global keyboard hotkeys (F2 DevTools, F3 Test Rig,
  // F4 Target Lifecycle, F8 chat pause, F9 schema-snap, Esc close-all,
  // Shift+T terminal, Numpad 1-9 ability rig). The hook was authored
  // long ago but its callsite here was lost; without this line every
  // keyboard hotkey listed in
  // docs/architecture/DEVTOOLS_AND_FCT_PIPELINE.md is dead.
  useGlobalInput();
  const npcTransactions = useNpcTransactions(player?.id);

  // The in-game right-click lag-attribution tooltip lives in
  // <LagAttributionOverlay/> (mounted in main.tsx) — keeps this gameplay
  // compositor off the diagnostics layer (CastLagStore).

  // Contextual quest button (gamepad Options/Start). PredictionLoop resolves
  // the nearest NPC and InteractionContext runs the normal talk path; by the
  // time the server payload lands here it can be hydrated into real quests
  // with live accepted/turn-in flags, which is the first point the accept-vs-
  // turn-in decision can actually be made. One-shot: cleared whether or not
  // anything was actionable, so a press never leaks into the next dialogue.
  const questActionPayload = interaction.npcDialoguePayload;
  useRecordDiscoveredQuests(questActionPayload, player);
  const consumePendingQuestAction = interaction.consumePendingQuestAction;
  useEffect(() => {
    const npcId = questActionPayload?.npcId;
    if (!npcId || !consumePendingQuestAction(npcId)) return;

    const playerLevel = player?.level || 1;
    const hydrated = hydrateNpcDialogue(
      questActionPayload,
      playerLevel,
      player?.quests,
      player?.class,
      player?.learnedAbilities,
      player,
    );
    const decision = resolveContextualQuestAction(hydrated?.quests, playerLevel);

    if (!decision) {
      gameLogger.info('ui', `[GameUIOverlay] Quest button: nothing actionable at ${npcId}`);
      return;
    }
    gameLogger.info('ui', `[GameUIOverlay] Quest button: ${decision.action} ${decision.questId}`);
    onAction(decision.action, decision.questId);
  }, [questActionPayload, player, consumePendingQuestAction, onAction]);

  // Only log when player actually changes, not on every render
  useEffect(() => {
    if (player) {
      gameLogger.info('ui', `[GameUIOverlay] Player active: ${player.name}`);
    }
  }, [player?.id, player?.name]);

  // Some network adapters update nested quest progress in place. Include the
  // meaningful quest values in the effect key so an objective completion can
  // promote ! → ? even when the QuestLog object identity is unchanged.
  const npcMarkerSnapshotKey = player
    ? [
      player.id,
      player.level,
      ...(player.quests?.active ?? []).map((quest) => `${quest.id}:${quest.completed}`),
      '|completed|',
      ...(player.quests?.completed ?? []),
    ].join('|')
    : 'no-player';

  // Bridge the authoritative React quest snapshot into the canvas NPC
  // renderer. NPCRenderer reads this small projection while positioning NPCs
  // each frame; quest acceptance/progress/turn-in therefore updates markers
  // without duplicating any server authority.
  useEffect(() => {
    // Quest-gated portal prompts read the same snapshot (lane BO); the stores
    // notify only when their sets change. Quests whose objectives are met open
    // sealed portals right away (the Coil exit after the Keeper falls).
    completedQuestStore.set(player?.quests?.completed ?? []);
    objectivesDoneQuestStore.set((player?.quests?.active ?? []).filter((q) => q.completed).map((q) => q.id));
    if (!player) {
      npcMarkerStore.reset();
      return;
    }

    npcMarkerStore.setPlayerState({
      level: player.level,
      activeQuests: player.quests?.active ?? [],
      completedQuestIds: player.quests?.completed ?? [],
    });

    return () => npcMarkerStore.reset();
  }, [player, npcMarkerSnapshotKey]);

  // Engine open-state for the spells + devTools windows — drives the HUD
  // active-indicators + open toggles now that they're engine windows.
  const spellsWindow = useWindow('spells');
  const devToolsWindow = useWindow('devTools');

  const handleActionWrapper = (action: string, data?: any) => {
    switch (action) {
    case 'toggle_devtools':
      windowBridge.toggle('devTools');
      break;
    case 'toggle_logger':
      windowBridge.toggle('logViewer');
      break;
    case 'toggle_editor':
      // Toggle editor mode via the manual-toggle event (useEditorMode listens).
      // The old synthetic-Backquote keydown was removed when backtick was
      // unbound (it crashed the client); this event is the surviving path.
      window.dispatchEvent(new Event('editor:manual-toggle'));
      break;

    case 'enable_editor':
      // Manual-enable path used by the DevTools Editor-tab button. Bypasses the
      // DevConfig.systems.editor gate so the editor can be flipped on from UI
      // even under presets that boot without editor enabled (ad-hoc MP tests).
      window.dispatchEvent(new Event('editor:manual-enable'));
      break;

    case 'disable_editor':
      window.dispatchEvent(new Event('editor:manual-disable'));
      break;

    case 'toggle_inventory':
      windowBridge.toggle('inventory');
      break;
    case 'toggle_quests':
      windowBridge.toggle('quests');
      break;
    case 'toggle_equipment':
      windowBridge.toggle('equipment');
      break;
    case 'toggle_spells':
      windowBridge.toggle('spells');
      break;
    case 'toggle_jobs':
      windowBridge.toggle('jobs');
      break;
    case 'toggle_talents':
      windowBridge.toggle('talents');
      break;
    // Diagnostic panels are now unified-engine windows (Phase 3). Toggle them
    // through the imperative windowBridge (same path the /tracker chat command
    // + DEV-BAR use) so there's one source of truth — the engine open-set.
    case 'toggle_ability_check':
      windowBridge.toggle('targetAbilityCheck');
      break;
    case 'toggle_lifecycle_monitor':
      windowBridge.toggle('abilityLifecycle');
      break;
    case 'toggle_visual_lifecycle':
      windowBridge.toggle('visualLifecycle');
      break;
    case 'toggle_target_lifecycle':
      windowBridge.toggle('targetLifecycle');
      break;
    case 'toggle_test_rig':
      windowBridge.toggle('abilityTestRig');
      break;
    case 'toggle_netobs_packet_log':
      windowBridge.toggle('netobsPacketLog');
      break;
    case 'toggle_netobs_latency_heatmap':
      windowBridge.toggle('netobsLatencyHeatmap');
      break;
    default:
      onAction(action, data);
    }
  };

  // Screensaver is "Alt+Z" — suppress ALL React game UI. Placed after every
  // hook above so the rules-of-hooks hold. The ScreensaverOverlay (caption +
  // watermark) lives in Game.tsx, separate from this tree.
  if (isScreensaverMode()) return null;

  // HudMoodProvider (HUD-restyle §6): one SCENE_MOOD subscription for the whole
  // gameplay HUD; exposes a clamped panel-base tint that the ink panels consume
  // via useHudMood(). Safe-neutral (and `?hudMood=0`-disable-able); mounting it
  // with no consumer is a visual no-op.
  const bar1 = player?.actionBar?.layout?.bars?.find((b) => b.id === 1);
  const touchCurrentPage = bar1?.currentPage ?? 1;
  const touchTotalPages = bar1?.pages ?? 1;
  const handleTouchPageFlip = useCallback(() => {
    const next = touchCurrentPage >= touchTotalPages ? 1 : touchCurrentPage + 1;
    handleActionWrapper('editActionBarLayout', { op: 'setPage', bar: 1, page: next });
  }, [touchCurrentPage, touchTotalPages, handleActionWrapper]);

  return (
    <HudMoodProvider>
      {!isEditorMode && !isScreensaverMode() && (
        <>
          <Minimap />
          <HUD
            player={player}
            isConnected={isConnected}
            isHotbarHydrated={isHotbarHydrated}
            disconnectReason={disconnectReason}
            levelName={currentLevelName}
            levelId={currentLevelId}
            onAction={handleActionWrapper}
            onOpenStats={() => windowBridge.toggle('equipment')}
            onOpenAbilities={spellsWindow.toggle}
            isDevToolsActive={devToolsWindow.open}
            isAbilitiesActive={spellsWindow.open}
          />

          <TouchControlsShell
            currentPage={touchCurrentPage}
            totalPages={touchTotalPages}
            onPageFlip={touchTotalPages > 1 ? handleTouchPageFlip : undefined}
          />
          <ContextualInteractionPrompt />
          <GameMenu heroName={player?.name} zoneName={currentLevelName} />

          {/* Lazy-loaded dev/debug panels — only loaded when first opened */}
          <React.Suspense fallback={null}>
          </React.Suspense>

          {/* Keep the empty status region mounted before the first FTUE beat so
              assistive technology observes later prompt text as a live update. */}
          <TutorialOverlay />
          {/* Opening narration (Pig Beach spawn) — self-gating: only plays on
              pig_beach load, once per fresh character (helix_* flag). */}
          <IntroNarrationOverlay />
          {/* Settings is now a unified-engine gameplay window (Phase 2 batch 1):
              mounted by WindowHost, opened from the QUICK MENU via windowBridge —
              no UIContext flag, no inline mount here. */}
          {/* Debug Info is a 🛠 launcher tool (debugInfo) — opened from the
              lower-right launcher, no UIContext flag. */}
          {/* Observational diagnostic panels (target/ability/cast lifecycle +
              netobs) moved to <DiagnosticsOverlay/> (mounted in main.tsx) so
              this gameplay compositor no longer imports diagnostic panels. */}

          {DevConfig.systems.playerList && <PlayerList currentPlayer={player} otherPlayers={otherPlayers} />}

          <ConnectionStatusOverlay />

          {/* GM Race debug overlay — ON by default while arc is in
              playtesting. Self-gates to gm_race_* maps + debug surface.
              Toggle via DebugToolbox "🏁 Race" button. */}
          <RaceDebugOverlay />

          {/* Persistent freeze diagnostics popup (opt-in via Settings -> Interface). */}
          <FreezeErrorOverlay />

          {/* Session-scoped "Return to Biome Nexus" — self-gates to the /biomes
              showcase tour (sessionStorage flag + current biome map). */}
          <BiomeNexusReturn />

          {/* Synced-SSOT God Mode pill — reads only Player.godMode via
              SERVER_STATE_UPDATE (server-authoritative), never a local flag. */}
          <GodModeIndicator />

          {(interaction.interactingNPC || interaction.shopState.isOpen) && <NPCWindowLayer>
            {interaction.interactingNPC && (() => {
            // Hydrate the server `talkToNPC` reply (real quests + shop) when it
            // has arrived; until then render the static NPC (name/role/greeting)
            // so the panel opens instantly.
              const hydrated = interaction.npcDialoguePayload
                ? hydrateNpcDialogue(
                  interaction.npcDialoguePayload,
                  player?.level || 1,
                  player?.quests,
                  player?.class,
                  player?.learnedAbilities,
                  player,
                )
                : null;
              const npc: NPC = hydrated ?? {
                id: interaction.interactingNPC.id,
                name: interaction.interactingNPC.name,
                level: player?.level || 1,
                type: interaction.interactingNPC.role === 'shop' ? 'shop'
                  : interaction.interactingNPC.role === 'quest_giver' ? 'quest'
                    : interaction.interactingNPC.role === 'trainer' ? 'trainer'
                      : interaction.interactingNPC.role === 'banker' ? 'banker' : 'story',
                dialog: {
                  greeting: npcQuestGreeting(interaction.interactingNPC.id, player?.quests, interaction.interactingNPC.dialogue.greeting),
                  options: interaction.interactingNPC.role === 'banker'
                    ? [{ text: 'Access Bank Vault', action: 'bank', icon: '💰' }]
                    : interaction.interactingNPC.id === 'guide_rafael'
                      ? [{ text: 'I am ready to begin!', action: 'complete_tutorial', icon: '⚔️' }]
                      : [],
                },
                quests: [],
                shopItems: [],
                services: [],
              };
              const completion = interaction.questCompletion?.npcId === npc.id
                ? { ...interaction.questCompletion, playerClass: player?.class } : null;
              // Shop AND quests are decoupled (their own windows); the dialogue
              // panel just shows greeting + options + "View Quests"/"Browse Shop".
              return (
                <>
                  <NPCInteractionUI
                    npc={npc as unknown as any}
                    playerLevel={player?.level || 1}
                    playerGold={player?.inventory?.gold ?? 0}
                    pendingAbilityId={npcTransactions.pending.learn?.id}
                    trainingRefusal={npcTransactions.refusals.learn?.npcId === npc.id ? { abilityId: npcTransactions.refusals.learn.id, message: npcTransactions.refusals.learn.message } : null}
                    onClose={interaction.closeNPCInteraction}
                    completion={completion}
                    onDialogSelect={option => dispatchNpcDialogueAction(npc.id, option, {
                      close: interaction.closeNPCInteraction, completeTutorial: interaction.completeTutorial,
                      openBank: () => windowBridge.open('bank'), send: onAction,
                      learn: abilityId => { npcTransactions.begin('learn', npc.id, abilityId,
                        () => onAction('learnAbility', { npcId: npc.id, abilityId })); },
                    })}
                    onViewQuests={() => interaction.openQuestOffer()}
                    onBrowseShop={() => interaction.openShop(npc.name, npc.shopItems as any, npc.id, npcQuestEntryCount(npc))}
                  />
                  {interaction.questOfferState.isOpen && npcQuestEntryCount(npc) > 0 && (
                    <QuestOfferWindow
                      npc={npc as unknown as any}
                      playerLevel={player?.level || 1}
                      onQuestAccept={(quest) => onAction('acceptQuest', quest.id)}
                      onQuestComplete={(quest) => onAction('completeQuest', quest.id)}
                      onClose={interaction.closeQuestOffer}
                      onBrowseShop={() => interaction.openShop(npc.name, npc.shopItems as any, npc.id, npcQuestEntryCount(npc))}
                      completion={completion}
                    />
                  )}
                </>
              );
            })()}

            {/* Shop is its own window now (decoupled from dialogue). Opened by the
              dialogue panel's "Browse Shop" button via interaction.openShop. */}
            {interaction.shopState.isOpen && (
              <VendorWindow
                npcId={interaction.shopState.npcId}
                npcName={interaction.shopState.npcName}
                pendingItemId={npcTransactions.pending.buy?.id}
                purchaseRefusal={npcTransactions.refusals.buy?.npcId === interaction.shopState.npcId ? { itemId: npcTransactions.refusals.buy.id, message: npcTransactions.refusals.buy.message } : null}
                items={interaction.shopState.items}
                playerGold={player?.inventory?.gold ?? 0}
                viewerLevel={player?.level}
                viewerClass={player?.class}
                questCount={interaction.shopState.questCount}
                onViewQuests={() => {
                  const npcId = interaction.shopState.npcId;
                  const npcName = interaction.shopState.npcName;
                  interaction.closeShop();
                  if (npcId) {
                    EventBus.getInstance().emit(EVENTS.NPC_INTERACT, {
                      npcId,
                      npcName: npcName || npcId,
                      npcType: 'shop',
                    });
                  }
                }}
                onPurchase={(item, quantity) => {
                  const payload = buildShopPurchasePayload(interaction.shopState, item.id, quantity, player?.inventory?.gold ?? 0);
                  if (payload) npcTransactions.begin('buy', payload.npcId, payload.itemId, () => onAction('buyItem', payload));
                }}
                onClose={interaction.closeShop}
              />
            )}
          </NPCWindowLayer>}
        </>
      )}
      {/* Editor Overlay - only render if editor system is enabled */}
      {DevConfig.systems.editor && isEditorMode && <EditorOverlay onExit={onExitEditor} />}
    </HudMoodProvider>
  );
};
